import { useEffect } from 'react'
import { Head, usePage, Link } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Button } from '@/components/ui/button'
import { ShieldCheck, Mail, Lock, Eye, CheckCircle2, ArrowRight } from 'lucide-react'
import type { PageProps } from '@/types'

export default function PrivacyPage() {
  const { auth } = usePage<PageProps>().props
  usePublicPage()

  useEffect(() => {
    document.title = 'Privacy Policy — PrepKiya'
  }, [])

  return (
    <>
      <Head title="Privacy Policy — PrepKiya">
        <meta
          name="description"
          content="Learn how PrepKiya protects student privacy, handles practice data, and maintains high security standards."
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 py-8 sm:py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 space-y-8 sm:space-y-12">
            
            {/* Header */}
            <div className="text-center space-y-2.5 sm:space-y-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#f4f7fb] text-[#131b2e] border border-slate-200 shadow-2xs">
                <ShieldCheck className="h-3.5 w-3.5 text-[#c8933b]" />
                <span>Security & Transparency</span>
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-[#131b2e] tracking-tight">
                Privacy Policy
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-slate-600 max-w-2xl mx-auto">
                Last updated: September 2026. Your privacy and academic data security are foundational to everything we build at PrepKiya.
              </p>
            </div>

            {/* Quick Principles Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-[#f4f7fb] border border-slate-200 flex items-center justify-center text-[#131b2e]">
                  <Lock className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">Zero Data Selling</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  We never sell, rent, or trade student personal records or test attempts to third-party marketers.
                </p>
              </div>

              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-[#c8933b]/15 border border-[#c8933b]/30 flex items-center justify-center text-[#c8933b]">
                  <Eye className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">Transparent Tracking</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  We only track metrics required to compute accurate percentiles, analytics, and time management stats.
                </p>
              </div>

              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">Student Control</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  You can update your account details, export results, or request complete account erasure anytime.
                </p>
              </div>
            </div>

            {/* Main Policy Content Sections */}
            <div className="space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-4 sm:p-8 md:p-10 shadow-xs leading-relaxed text-xs sm:text-sm text-slate-800">
              
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#131b2e] flex items-center gap-2">
                  <span className="text-[#c8933b] font-mono text-sm">01.</span> Information We Collect
                </h2>
                <p className="text-slate-600">
                  To provide an authentic examination environment and personalized analytics, we collect the following categories of data:
                </p>
                <ul className="list-disc list-inside space-y-1.5 pl-2 text-slate-600">
                  <li><strong>Account Credentials:</strong> Your name, email address, password hash, and optional profile preferences or avatar.</li>
                  <li><strong>Exam Performance & Interaction:</strong> Selected test answers, time spent per section/question, marks scored, bookmarks, and test attempt history.</li>
                  <li><strong>Wallet & Credit Activity:</strong> Credit grants, purchases, test unlocks, and transaction timestamps.</li>
                  <li><strong>Technical Diagnostics:</strong> Browser version, IP address, and session tokens used strictly to safeguard exam sessions against unauthorized multi-device sharing.</li>
                </ul>
              </section>

              <div className="h-px w-full bg-slate-100" />

              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#131b2e] flex items-center gap-2">
                  <span className="text-[#c8933b] font-mono text-sm">02.</span> How We Use Your Information
                </h2>
                <p className="text-slate-600">
                  All collected data serves direct learning and assessment objectives:
                </p>
                <ul className="list-disc list-inside space-y-1.5 pl-2 text-slate-600">
                  <li>Generating real-time rank predictions, percentile distributions, and category-wise strength/weakness heatmaps.</li>
                  <li>Ensuring fair test environments through timer synchronization and auto-submission rules.</li>
                  <li>Calibrating item discrimination indices and question quality scores (aggregated anonymously).</li>
                  <li>Delivering transactional notifications, score summaries, and account recovery instructions.</li>
                </ul>
              </section>

              <div className="h-px w-full bg-slate-100" />

              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#131b2e] flex items-center gap-2">
                  <span className="text-[#c8933b] font-mono text-sm">03.</span> Data Security & Storage
                </h2>
                <p className="text-slate-600">
                  We employ industry-standard engineering practices to protect your data. All communication between your device and our servers is encrypted using modern TLS/HTTPS. Passwords are never stored in plain text and are secured using salted one-way cryptographic hashing (Bcrypt). Production databases are hosted in isolated virtual private cloud environments with regular automated backups.
                </p>
              </section>

              <div className="h-px w-full bg-slate-100" />

              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#131b2e] flex items-center gap-2">
                  <span className="text-[#c8933b] font-mono text-sm">04.</span> Third-Party Services
                </h2>
                <p className="text-slate-600">
                  We integrate only with reputable infrastructure partners necessary to operate the platform:
                </p>
                <ul className="list-disc list-inside space-y-1.5 pl-2 text-slate-600">
                  <li><strong>Authentication:</strong> Google OAuth (if you choose to sign in with Google).</li>
                  <li><strong>Transactional Email:</strong> Secure SMTP gateways used exclusively to send your test reports and password resets.</li>
                </ul>
                <p className="text-slate-600 pt-1">
                  We do not share your private test scores or personal information with advertisers, third-party coaching networks, or commercial brokers.
                </p>
              </section>

              <div className="h-px w-full bg-slate-100" />

              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-[#131b2e] flex items-center gap-2">
                  <span className="text-[#c8933b] font-mono text-sm">05.</span> Student Rights & Account Deletion
                </h2>
                <p className="text-slate-600">
                  You maintain full rights to access, amend, or remove your profile information. If you wish to delete your PrepKiya account and associated test attempts permanently, email our data team at <strong className="text-[#131b2e]">prepkiya@gmail.com</strong> from your registered email address, and we will process your deletion within 3 business days.
                </p>
              </section>

            </div>

            {/* Direct Contact Card */}
            <div className="rounded-2xl sm:rounded-3xl border border-[#c8933b]/30 bg-[#f8fafc] p-4 sm:p-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-6 shadow-xs">
              <div className="space-y-1 text-center sm:text-left">
                <h2 className="text-base sm:text-lg font-bold text-[#131b2e]">
                  Have questions about this Privacy Policy?
                </h2>
                <p className="text-xs sm:text-sm text-slate-600">
                  Our privacy and grievance team is directly reachable at <strong className="text-[#b5802a]">prepkiya@gmail.com</strong>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 shrink-0 w-full sm:w-auto">
                <Button asChild className="bg-[#131b2e] text-white hover:bg-[#1e293b] font-bold rounded-xl px-5 h-10 text-xs shadow-xs w-full sm:w-auto justify-center">
                  <a href="mailto:prepkiya@gmail.com?subject=PrepKiya%20Privacy%20Question">
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Email Support
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-slate-200 text-slate-800 bg-white hover:bg-slate-50 font-bold rounded-xl px-4 h-10 text-xs w-full sm:w-auto justify-center">
                  <Link href="/help">
                    Help Center
                    <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
